/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Container } from "@/components/ui-kit";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";
import { fetchCustomerWebsites, fetchSiteCapabilities } from "@/lib/api";

// §4 — one shared shell reused by every flat /account/websites/:slug/* route (this
// codebase has no nested React Router routes anywhere — see the plan notes — so each
// tab is its own top-level <Route>, all wrapping this same component rather than a
// router-level layout/outlet).
const TABS = [
  { key: "overview", label: "Overview", to: "/overview" },
  { key: "agenda", label: "Agenda", to: "/agenda", capability: "appointments" },
  { key: "customers", label: "Customers", to: "/customers", capability: "customers" },
  { key: "inventory", label: "Inventory", to: "/inventory", capability: "inventory" },
  { key: "orders", label: "Orders", to: "/orders", capability: "orders" },
  { key: "shipping", label: "Shipping", to: "/shipping", capability: "shipping" },
  { key: "analytics", label: "Analytics", to: "/analytics", capability: "analytics" },
  { key: "reminders", label: "Reminders", to: "/reminders" },
  { key: "tax-calendar", label: "Tax Calendar", to: "/tax-calendar", capability: "tax_calendar" },
  { key: "billing", label: "Billing", to: "/billing", capability: "billing" },
];

export function useSiteDashboard() {
  const { slug } = useParams();
  const { token, checking, isLoggedIn } = useCustomerAuth();
  const [site, setSite] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    fetchCustomerWebsites(token).then((sites) => setSite(sites.find((s) => s.slug === slug) || null)).catch(() => {});
    fetchSiteCapabilities(token, slug).then(setCapabilities).catch(() => setError("Could not load this website's dashboard."));
  }, [token, slug]);

  return { slug, token, checking, isLoggedIn, site, capabilities, error };
}

export default function SiteDashboardShell({ active, dashboard, children }) {
  const { slug, checking, isLoggedIn, site, capabilities, error } = dashboard;

  if (checking) return null;
  if (!isLoggedIn) return <Navigate to="/account/login" replace />;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Container className="py-12 max-w-5xl">
        <Link to="/account/dashboard" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#10B981] mb-4"><ArrowLeft size={15} /> My Websites</Link>
        <h1 className="text-2xl font-bold font-display text-[#0F172A]">{site?.business_name || slug}</h1>
        {site?.category && <p className="mt-0.5 text-xs uppercase tracking-wide text-[#94A3B8]">{site.category}</p>}

        <nav className="mt-6 flex gap-1 overflow-x-auto rounded-xl border border-[#E2E8F0] bg-white p-1" data-testid="site-dashboard-nav">
          {TABS.filter((t) => !t.capability || !capabilities || capabilities[t.capability]).map((t) => (
            <Link key={t.key} to={`/account/websites/${slug}${t.to}`}
              className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${active === t.key ? "bg-[#0F172A] text-white" : "text-[#475569] hover:bg-[#F1F5F9]"}`}>
              {t.label}
            </Link>
          ))}
        </nav>

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        <div className="mt-8">{children}</div>
      </Container>
    </div>
  );
}
